import { Injectable } from '@angular/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class GtmService {

  constructor(private gtmService: GoogleTagManagerService) { }

  /**
   * Envía un evento personalizado a Google Tag Manager
   * @param eventData - Los datos del evento a enviar
   */
  pushEvent(eventData: any): void {
    this.gtmService.pushTag(eventData);
  }

  /**
   * Envía un evento a todos los contenedores de GTM configurados
   * @param eventData - Los datos del evento a enviar
   */
  pushEventToAllContainers(eventData: any): void {
    // Envía al dataLayer global (todos los contenedores lo recibirán)
    this.gtmService.pushTag(eventData);
    
    // También podemos enviar específicamente a cada contenedor si es necesario
    if (environment.gtmIds && environment.gtmIds.length > 0) {
      environment.gtmIds.forEach(gtmId => {
        // Los eventos se envían automáticamente a todos los contenedores
        // que comparten el mismo dataLayer
        console.log(`Evento enviado a contenedor: ${gtmId}`, eventData);
      });
    }
  }

  /**
   * Envía evento de navegación/página
   * @param pageName - Nombre de la página
   * @param pageUrl - URL de la página
   */
  pushPageView(pageName: string, pageUrl: string): void {
    this.gtmService.pushTag({
      event: 'page_view',
      page_name: pageName,
      page_url: pageUrl,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Envía evento de flujo específico para Sufi
   * @param flowName - Nombre del flujo
   * @param step - Paso del flujo
   * @param additionalData - Datos adicionales opcionales
   */
  pushSufiFlowEvent(flowName: string, step: string, additionalData?: any): void {
    const eventData = {
      nombreflujo: flowName,
      event: 'FLUJO_SUFI_PAGAR',
      paso: step,
      timestamp: new Date().toISOString(),
      ...additionalData
    };
    
    this.pushEventToAllContainers(eventData);
  }

  /**
   * Envía información de usuario hasheada
   * @param docType - Tipo de documento hasheado
   * @param userId - ID de usuario hasheado
   */
  pushUserInfo(docType: string, userId: string): void {
    const eventData = {
      event: 'SEND_USERID',
      tipo_doc: docType,
      userId: userId,
      timestamp: new Date().toISOString()
    };
    
    this.pushEventToAllContainers(eventData);
  }

  /**
   * Envía evento de error
   * @param errorType - Tipo de error
   * @param errorMessage - Mensaje de error
   * @param step - Paso donde ocurrió el error
   */
  pushErrorEvent(errorType: string, errorMessage: string, step?: string): void {
    const eventData = {
      event: 'error_event',
      error_type: errorType,
      error_message: errorMessage,
      step: step,
      timestamp: new Date().toISOString()
    };
    
    this.pushEventToAllContainers(eventData);
  }

  /**
   * Envía evento de conversión/éxito de pago
   * @param transactionId - ID de la transacción
   * @param amount - Monto de la transacción
   * @param paymentMethod - Método de pago utilizado
   */
  pushPaymentSuccess(transactionId: string, amount: number, paymentMethod: string): void {
    const eventData = {
      event: 'payment_success',
      transaction_id: transactionId,
      value: amount,
      currency: 'COP',
      payment_method: paymentMethod,
      timestamp: new Date().toISOString()
    };
    
    this.pushEventToAllContainers(eventData);
  }

  /**
   * Envía evento de inicio de pago
   * @param amount - Monto del pago
   * @param paymentType - Tipo de pago
   */
  pushPaymentStart(amount: number, paymentType: string): void {
    const eventData = {
      event: 'begin_checkout',
      value: amount,
      currency: 'COP',
      payment_type: paymentType,
      timestamp: new Date().toISOString()
    };
    
    this.pushEventToAllContainers(eventData);
  }

  /**
   * Envía un evento a un contenedor específico de GTM
   * @param eventData - Los datos del evento a enviar
   * @param gtmId - ID específico del contenedor GTM
   */
  pushEventToSpecificContainer(eventData: any, gtmId: string): void {
    // Agregar el GTM ID al evento para identificarlo
    const eventWithContainer = {
      ...eventData,
      gtm_container_id: gtmId
    };
    
    this.gtmService.pushTag(eventWithContainer);
    console.log(`Evento específico enviado a contenedor ${gtmId}:`, eventWithContainer);
  }

  /**
   * Obtiene la lista de contenedores GTM configurados
   */
  getConfiguredContainers(): string[] {
    return environment.gtmIds || [environment.gtmId];
  }

  /**
   * Verifica si un contenedor específico está configurado
   * @param gtmId - ID del contenedor a verificar
   */
  isContainerConfigured(gtmId: string): boolean {
    const containers = this.getConfiguredContainers();
    return containers.includes(gtmId);
  }
}
